import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../components/user/Layout';
import authManager from '../../utils/authManager';
import axios from '../../utils/axios';
import { formatCurrency } from '../../utils/helper';

function PaymentWallet() {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [showFutureModal, setShowFutureModal] = useState(false);
  const [selectedYears, setSelectedYears] = useState([]);
  const [customYear, setCustomYear] = useState('');
  const [customYearAmount, setCustomYearAmount] = useState(null);
  const [futurePayments, setFuturePayments] = useState([]);
  const [activeTab, setActiveTab] = useState('preset'); 

  const user = authManager.getUserProfile();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const txnId = params.get('transaction_id');
    if (txnId) {
      checkPaymentStatus(txnId);
    }
  }, [location]);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axios.get('member/payments');
      if (response.data.success) {
        setPaymentData(response.data.data);
      } else {
        setError('Failed to load payment data');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (transactionId) => {
    setProcessing(true);
    try {
      const response = await axios.get('payment/status', {
        params: { transaction_id: transactionId }
      });
      if (response.data.success) {
        const status = response.data.data.status;
        if (status === 'completed') {
          setStatusMessage({ type: 'success', text: 'Payment completed successfully!' });
          await fetchPayments();
          setSelectedIds([]);
        } else if (status === 'failed') {
          setStatusMessage({ type: 'error', text: 'Payment failed. Please try again.' });
        } else {
          setStatusMessage({ type: 'info', text: `Payment status: ${status}` });
        }
      } else {
        setStatusMessage({ type: 'error', text: 'Failed to verify payment status.' });
      }
    } catch (err) {
      setStatusMessage({ type: 'error', text: err.response?.data?.message || 'Error checking status' });
    } finally {
      setProcessing(false);
      navigate('/payment-wallet', { replace: true });
    }
  };

  const handleSelect = (paymentId) => {
    const id = Number(paymentId);
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  // Select/deselect all unpaid pending payments
  const handleSelectAll = () => {
    if (!paymentData) return;
    const unpaidIds = paymentData.pending_payments.map(p => Number(p.id));
    if (selectedIds.length === unpaidIds.length && unpaidIds.every(id => selectedIds.includes(id))) {
      setSelectedIds([]);
    } else {
      setSelectedIds(unpaidIds);
    }
  };

  // Initialize payment for selected pending payments
  const handlePaySelected = async () => {
    if (selectedIds.length === 0) {
      alert('Please select at least one payment.');
      return;
    }

    setProcessing(true);
    setError(null);
    try {
      const response = await axios.post('payment/initialize', {
        member_payment_ids: selectedIds.map(id => Number(id))
      });
      if (response.data.success) {
        const { redirect_url } = response.data.data;
        window.location.href = redirect_url;
      } else {
        setError(response.data.message || 'Payment initialization failed.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setProcessing(false);
    }
  };

  // Handle future year selection in modal
  const handleYearSelect = (year) => {
    setSelectedYears(prev => {
      if (prev.includes(year)) {
        return prev.filter(y => y !== year);
      } else {
        return [...prev, year];
      }
    });
  };

  // Handle select all future years
  const handleSelectAllFuture = () => {
    if (selectedYears.length === futurePayments.length) {
      setSelectedYears([]);
    } else {
      setSelectedYears(futurePayments.map(p => p.year));
    }
  };

  const handleCustomYearChange = async (e) => {
    const year = e.target.value;
    setCustomYear(year);
    
    if (year && year >= new Date().getFullYear()) {
      try {
        const response = await axios.get(`payment/amount/${year}`, {
          params: { category_id: paymentData?.member?.category_id }
        });
        if (response.data.success) {
          setCustomYearAmount(response.data.data.amount);
        } else {
          setCustomYearAmount(null);
          setError(response.data.message);
        }
      } catch (err) {
        setCustomYearAmount(null);
        setError(err.response?.data?.message || 'Failed to fetch amount for this year');
      }
    } else {
      setCustomYearAmount(null);
    }
  };

  const handleCustomYearPayment = async () => {
    if (!customYear) {
      alert('Please enter a year.');
      return;
    }
    
    const currentYear = new Date().getFullYear();
    if (parseInt(customYear) < currentYear) {
      alert('Please enter a future year.');
      return;
    }

    setProcessing(true);
    setError(null);
    
    try {
      const response = await axios.post('payment/future', {
        year: parseInt(customYear),
        category_id: paymentData.member.category_id
      });
      
      if (response.data.success) {
        const { redirect_url } = response.data.data;
        window.location.href = redirect_url;
      } else {
        setError(response.data.message || 'Failed to initialize future payment');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setProcessing(false);
      setShowFutureModal(false);
      setCustomYear('');
      setCustomYearAmount(null);
    }
  };

  const handlePresetPayments = async () => {
    if (selectedYears.length === 0) {
      alert('Please select at least one year.');
      return;
    }

    setProcessing(true);
    setError(null);
    
    try {
      const firstYear = selectedYears[0];
      const response = await axios.post('payment/future', {
        year: parseInt(firstYear),
        category_id: paymentData.member.category_id
      });
      
      if (response.data.success) {
        const { redirect_url } = response.data.data;
        window.location.href = redirect_url;
      } else {
        setError(response.data.message || 'Failed to initialize future payment');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setProcessing(false);
      setShowFutureModal(false);
      setSelectedYears([]);
    }
  };

  const openFutureModal = () => {
    if (paymentData && paymentData.all_future_years) {
      setFuturePayments(paymentData.all_future_years);
      setSelectedYears([]);
      setCustomYear('');
      setCustomYearAmount(null);
      setActiveTab('preset');
      setShowFutureModal(true);
    } else {
      setError('No future years available for payment');
    }
  };

  const calculateTotalSelected = () => {
    if (!paymentData) return 0;
    return paymentData.pending_payments
      .filter(p => selectedIds.includes(Number(p.id)))
      .reduce((sum, p) => sum + p.amount, 0);
  };

  const calculateFutureTotal = () => {
    return futurePayments
      .filter(p => selectedYears.includes(p.year))
      .reduce((sum, p) => sum + p.amount, 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (error && !paymentData) {
    return (
      <Layout>
        <div className="text-center text-red-600 py-10">{error}</div>
      </Layout>
    );
  }

  const { member, summary, payments, pending_payments, all_future_years } = paymentData;
  const totalPending = pending_payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
  const selectedTotal = calculateTotalSelected();

  return (
    <Layout>
      <div className="min-h-screen py-12 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto space-y-10">
          {/* Status Message */}
          {statusMessage && (
            <div className={`p-4 rounded-lg ${statusMessage.type === 'success' ? 'bg-green-100 text-green-700' :
                statusMessage.type === 'error' ? 'bg-red-100 text-red-700' :
                  'bg-blue-100 text-blue-700'
              }`}>
              <div className="flex justify-between items-center">
                <span>{statusMessage.text}</span>
                <button 
                  onClick={() => setStatusMessage(null)} 
                  className="text-xl font-bold hover:opacity-70"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-3xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">💰 Payment Wallet</h1>
            <p className="text-gray-600">
              {member.name} ({member.union_code}) – {member.department}
            </p>
            {member.category_name && (
              <p className="text-sm text-gray-500 mt-1">
                Category: <span className="font-semibold">{member.category_name}</span>
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-pink-500 to-violet-600 text-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition duration-300">
              <p className="text-lg opacity-90">Member Since</p>
              <p className="text-3xl font-bold">{new Date(user?.doj).toLocaleDateString('en-IN')}</p>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition duration-300">
              <p className="text-sm opacity-90">Total Paid</p>
              <p className="text-3xl font-bold">₹{formatCurrency(summary.total_paid)}</p>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition duration-300">
              <p className="text-sm opacity-90">Total Due</p>
              <p className="text-3xl font-bold">₹{formatCurrency(summary.total_due)}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition duration-300">
              <p className="text-sm opacity-90">Pending Years</p>
              <p className="text-3xl font-bold">{summary.pending_count} Years</p>
            </div>
          </div>

          {pending_payments && pending_payments.length > 0 && (
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-pink-600">
                <h2 className="text-xl font-semibold text-white">📋 Pending Payments Summary</h2>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {pending_payments.map((pending, index) => (
                    <div key={index} className="flex justify-between items-center border-b border-gray-200 py-3 hover:bg-gray-50 transition">
                      <div>
                        <p className="font-semibold text-gray-800">{pending.period}</p>
                        {pending.category_name && (
                          <p className="text-sm text-gray-500">Category: {pending.category_name}</p>
                        )}
                        <p className="text-sm text-gray-500">Due Date: {pending.due_date || 'Not specified'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-600">₹{formatCurrency(pending.amount)}</p>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-4 border-t-2 border-gray-300 mt-4">
                    <p className="text-lg font-bold text-gray-800">Total Pending Amount</p>
                    <p className="text-2xl font-bold text-red-600">₹{formatCurrency(totalPending)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 flex justify-between items-center flex-wrap gap-4">
              <h2 className="text-xl font-semibold text-white">📜 Payment History</h2>
              <div className="flex items-center space-x-4">
                {pending_payments && pending_payments.length > 0 && (
                  <>
                    <button
                      onClick={handleSelectAll}
                      className="text-sm text-white bg-white/20 hover:bg-white/30 px-4 py-2 rounded-full transition"
                    >
                      {selectedIds.length === pending_payments.length ? 'Deselect All' : 'Select All Unpaid'}
                    </button>
                    <button
                      onClick={handlePaySelected}
                      disabled={processing || selectedIds.length === 0}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                        processing || selectedIds.length === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-white text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      {processing ? 'Processing...' : `Pay Selected (₹${formatCurrency(selectedTotal)})`}
                    </button>
                  </>
                )}
                <button
                  onClick={openFutureModal}
                  className="px-4 py-2 rounded-full text-sm font-semibold transition bg-green-500 text-white hover:bg-green-600"
                >
                  🚀 Pay Future Years
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {payments && payments.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {payments.map((payment) => (
                    <div
                      key={payment.id}
                      className={`bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 relative ${
                        payment.is_paid ? 'border-green-200 bg-green-50' : 'border-gray-200'
                      }`}
                    >
                      {!payment.is_paid && (
                        <div className="absolute top-3 right-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(Number(payment.id))}
                            onChange={() => handleSelect(payment.id)}
                            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                          />
                        </div>
                      )}

                      <div className="space-y-3">
                        <div className="pr-6">
                          <div className="text-sm font-medium text-gray-500">Valid Period</div>
                          <div className="text-base font-semibold text-gray-800">
                            {payment.period}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm font-medium text-gray-500">Amount</div>
                          <div className="text-lg font-bold text-gray-900">
                            ₹{formatCurrency(payment.amount)}
                          </div>
                        </div>

                        {payment.category_name && (
                          <div>
                            <div className="text-sm font-medium text-gray-500">Category</div>
                            <div className="text-sm text-gray-600">{payment.category_name}</div>
                          </div>
                        )}

                        <div>
                          <div className="text-sm font-medium text-gray-500">Status</div>
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                              payment.is_paid
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {payment.is_paid ? '✓ Paid' : '⏳ Unpaid'}
                          </span>
                        </div>

                        {payment.paid_date && (
                          <div>
                            <div className="text-sm font-medium text-gray-500">Paid Date</div>
                            <div className="text-sm text-gray-600">
                              {new Date(payment.paid_date).toLocaleDateString('en-IN')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No payment records found.</p>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-100 text-red-700 p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Future Payment Modal */}
      {showFutureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Pay Future Membership</h3>
                  <p className="text-gray-600 mt-1">Choose a year to pay for future membership</p>
                </div>
                <button
                  onClick={() => {
                    setShowFutureModal(false);
                    setSelectedYears([]);
                    setCustomYear('');
                    setCustomYearAmount(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex px-6">
                <button
                  onClick={() => setActiveTab('preset')}
                  className={`py-3 px-4 font-medium transition ${
                    activeTab === 'preset'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📅 Preset Years
                </button>
                <button
                  onClick={() => setActiveTab('custom')}
                  className={`py-3 px-4 font-medium transition ${
                    activeTab === 'custom'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  ✏️ Custom Year
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Preset Years Tab */}
              {activeTab === 'preset' && (
                <>
                  {futurePayments.length > 0 ? (
                    <>
                      <div className="mb-4 flex justify-between items-center">
                        <button
                          onClick={handleSelectAllFuture}
                          className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
                        >
                          {selectedYears.length === futurePayments.length ? 'Deselect All' : 'Select All'}
                        </button>
                        <p className="text-sm text-gray-500">
                          Selected: <span className="font-semibold">{selectedYears.length}</span> years
                        </p>
                      </div>

                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {futurePayments.map((yearData) => (
                          <div
                            key={yearData.year}
                            className={`border rounded-lg p-4 cursor-pointer transition ${
                              selectedYears.includes(yearData.year)
                                ? 'border-blue-500 bg-blue-50 shadow-md'
                                : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                            }`}
                            onClick={() => handleYearSelect(yearData.year)}
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={selectedYears.includes(yearData.year)}
                                  onChange={() => handleYearSelect(yearData.year)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                                />
                                <div>
                                  <p className="font-semibold text-gray-800 text-lg">{yearData.period}</p>
                                  <p className="text-sm text-gray-500">Financial Year</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-xl font-bold text-gray-900">₹{formatCurrency(yearData.amount)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {selectedYears.length > 0 && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <div className="flex justify-between items-center mb-4">
                            <p className="text-lg font-semibold text-gray-800">Total Amount</p>
                            <p className="text-2xl font-bold text-blue-600">
                              ₹{formatCurrency(calculateFutureTotal())}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={handlePresetPayments}
                          disabled={processing || selectedYears.length === 0}
                          className={`flex-1 py-3 rounded-lg font-semibold transition ${
                            processing || selectedYears.length === 0
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {processing ? (
                            <div className="flex items-center justify-center space-x-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                              <span>Processing...</span>
                            </div>
                          ) : (
                            `Pay ₹${formatCurrency(calculateFutureTotal())}`
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setShowFutureModal(false);
                            setSelectedYears([]);
                          }}
                          className="flex-1 py-3 rounded-lg font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No preset future years available.</p>
                      <p className="text-sm text-gray-400 mt-2">You can use the "Custom Year" tab to enter any year.</p>
                    </div>
                  )}
                </>
              )}

              {/* Custom Year Tab */}
              {activeTab === 'custom' && (
                <div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter Financial Year
                    </label>
                    <input
                      type="number"
                      value={customYear}
                      onChange={handleCustomYearChange}
                      placeholder="e.g., 2026"
                      min={new Date().getFullYear()}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      Enter any future year (e.g., 2026 for 2025-2026, 2027 for 2026-2027)
                    </p>
                  </div>

                  {customYear && customYearAmount && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm text-gray-600">Period</p>
                          <p className="text-lg font-semibold text-gray-800">
                            {parseInt(customYear) - 1} - {customYear}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Amount</p>
                          <p className="text-2xl font-bold text-blue-600">₹{formatCurrency(customYearAmount)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {customYear && !customYearAmount && customYear >= new Date().getFullYear() && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                      <p className="text-yellow-800">
                        ⚠️ Amount not configured for this year. Please contact administrator.
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handleCustomYearPayment}
                      disabled={processing || !customYear || !customYearAmount || customYear < new Date().getFullYear()}
                      className={`flex-1 py-3 rounded-lg font-semibold transition ${
                        processing || !customYear || !customYearAmount || customYear < new Date().getFullYear()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {processing ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Processing...</span>
                        </div>
                      ) : (
                        `Pay ₹${formatCurrency(customYearAmount || 0)}`
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowFutureModal(false);
                        setCustomYear('');
                        setCustomYearAmount(null);
                      }}
                      className="flex-1 py-3 rounded-lg font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default PaymentWallet;